import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Sparkles, X } from 'lucide-react';
import { ladeRundgang, rundgangErledigt } from '@/api/rundgang.api';
import { useTreeContextStore } from '@/store/useTreeContextStore';
import { START_SCHRITTE, funktionsSchritte, type Schritt } from './schritte';

// ─── Der geführte Rundgang ───────────────────────────────────────────────────
//
// Ein Kasten, der auf etwas zeigt. Die Stelle, auf die er zeigt, bleibt hell,
// der Rest liegt unter einem dunklen Schleier – so ist ohne Pfeilspitze klar,
// wovon die Rede ist.
//
// Zwei Eigenschaften, an denen so etwas üblicherweise scheitert:
//
//   FEHLENDE ANKER   Die Kopfzeile zeigt je nach Fensterbreite andere
//                    Bedienelemente. Ein Schritt, dessen Element gerade nicht
//                    sichtbar ist, wird deshalb nicht mit einem Kasten im
//                    Nirgendwo gezeigt, sondern erscheint mittig.
//   WEGGEKLICKT      Escape, der Schleier und „Überspringen" führen alle zum
//                    selben Ergebnis: weg, und zwar dauerhaft. Ein Rundgang,
//                    den man dreimal wegklicken muss, ist eine Belästigung.

/** Wo der Kasten sitzt: an einem Element oder in der Mitte. */
interface Platz {
  oben: number;
  links: number;
  breite: number;
  hoehe: number;
}

const RAND = 8;
const KASTEN_BREITE = 320;

function suchePlatz(anker: string | string[] | undefined): Platz | null {
  if (!anker) return null;
  for (const name of Array.isArray(anker) ? anker : [anker]) {
    for (const el of document.querySelectorAll<HTMLElement>(`[data-rundgang="${name}"]`)) {
      const r = el.getBoundingClientRect();
      // Unsichtbare Elemente haben in dieser Oberfläche keine Ausdehnung –
      // die Kopfzeile blendet je nach Breite verschiedene Knöpfe aus.
      if (r.width > 0 && r.height > 0) {
        return { oben: r.top, links: r.left, breite: r.width, hoehe: r.height };
      }
    }
  }
  return null;
}

/** Wohin der Kasten passt, ohne aus dem Fenster zu ragen. */
function kastenPosition(platz: Platz | null): { top: number; left: number } | null {
  if (!platz) return null;
  const links = Math.min(
    Math.max(RAND, platz.links + platz.breite / 2 - KASTEN_BREITE / 2),
    window.innerWidth - KASTEN_BREITE - RAND,
  );
  const unterhalb = platz.oben + platz.hoehe + 12;
  return { top: unterhalb, left: links };
}

interface Props {
  /**
   * Wird gerufen, wenn der Rundgang losgeht. Gedacht zum Schließen offener
   * Menüs: der Rundgang liegt über allem, und ein Menü darunter wäre sichtbar,
   * aber nicht mehr bedienbar.
   */
  onStart?: () => void;
  /** Wird gerufen, wenn er durch ist – abgeschlossen wie übersprungen. */
  onEnde?: () => void;
}

export function Rundgang({ onStart, onEnde }: Props) {
  const queryClient = useQueryClient();
  const activeTreeId = useTreeContextStore((s) => s.activeTreeId);

  const { data } = useQuery({
    queryKey: ['rundgang', activeTreeId],
    queryFn: ladeRundgang,
    enabled: Boolean(activeTreeId),
    // Der Rundgang ist eine Begrüßung, kein Live-Zustand: einmal je Sitzung
    // reicht, und ein Nachladen mitten im Ablauf würde den Kasten umspringen
    // lassen.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const [nr, setNr] = useState(0);
  const [platz, setPlatz] = useState<Platz | null>(null);
  const [läuft, setLäuft] = useState(false);

  // useMemo ist hier kein Feinschliff, sondern notwendig: funktionsSchritte()
  // baut die Liste jedes Mal neu, das neue Objekt löste die Positionsmessung
  // aus, die ein Rendern auslöste – und React brach nach ein paar tausend
  // Runden mit „Maximum update depth exceeded" ab. Der Start-Rundgang blieb
  // heil, weil seine Schritte eine Konstante sind; aufgefallen ist es erst am
  // Funktions-Rundgang.
  const schritte: Schritt[] = useMemo(
    () => (data?.offen === 'funktionen' ? funktionsSchritte(data.neu) : START_SCHRITTE),
    [data?.offen, data?.neu],
  );

  // Der Callback liegt in einer Ref, nicht in den Abhängigkeiten: eine inline
  // übergebene Funktion wäre bei jedem Rendern neu, der Effekt liefe erneut –
  // und setNr(0) würfe den Nutzer mitten im Rundgang auf Schritt eins zurück.
  const beiStart = useRef(onStart);
  beiStart.current = onStart;
  const beiEnde = useRef(onEnde);
  beiEnde.current = onEnde;

  // Erst starten, wenn der Server geantwortet hat – sonst blitzt der Kasten
  // auf und verschwindet wieder, weil doch nichts ansteht.
  useEffect(() => {
    if (!data?.offen) return;
    setNr(0);
    setLäuft(true);
    beiStart.current?.();
  }, [data]);

  const aktuell = schritte[nr];

  // Der Effekt hängt am Anker als ZEICHENKETTE, nicht am Schritt-Objekt und
  // auch nicht an der Anker-Liste: beide wären bei jedem Render neu, würden die
  // Messung auslösen, damit ein Rendern – und React bräche nach ein paar
  // tausend Runden ab. Genau so ist es beim ersten Anlauf passiert.
  const anker = aktuell?.anker;
  const ankerSchluessel = Array.isArray(anker) ? anker.join('|') : (anker ?? '');

  // useLayoutEffect statt useEffect: die Position steht damit vor dem Zeichnen
  // fest, sonst erschiene der Kasten für einen Bildaufbau lang an der alten
  // Stelle.
  useLayoutEffect(() => {
    if (!läuft) return;
    const messen = () => setPlatz(suchePlatz(anker));
    messen();
    window.addEventListener('resize', messen);
    window.addEventListener('scroll', messen, true);
    return () => {
      window.removeEventListener('resize', messen);
      window.removeEventListener('scroll', messen, true);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [läuft, ankerSchluessel]);

  const abhaken = useMutation({
    mutationFn: () =>
      rundgangErledigt(data?.offen ?? 'start', data?.neu ?? []),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['rundgang'] });
    },
  });

  // Ein Weg hinaus, für alle drei Arten hinauszugehen: Fertig, Überspringen,
  // Escape. Wer wegklickt, hat entschieden – auch das wird vermerkt, sonst
  // stünde derselbe Rundgang bei der nächsten Anmeldung wieder da.
  const { mutate: abhakenJetzt } = abhaken;
  const beenden = useCallback(() => {
    setLäuft(false);
    abhakenJetzt();
    beiEnde.current?.();
  }, [abhakenJetzt]);

  const weiter = useCallback(() => {
    setNr((n) => {
      if (n + 1 < schritte.length) return n + 1;
      return n;
    });
  }, [schritte.length]);

  useEffect(() => {
    if (!läuft) return;
    const taste = (e: KeyboardEvent) => {
      if (e.key === 'Escape') beenden();
      if (e.key === 'ArrowRight') weiter();
      if (e.key === 'ArrowLeft') setNr((n) => Math.max(0, n - 1));
    };
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [läuft, beenden, weiter]);

  if (!läuft || !aktuell || schritte.length === 0) return null;

  const letzter = nr === schritte.length - 1;
  const pos = kastenPosition(platz);

  return createPortal(
    <div className="fixed inset-0 z-[2000]" role="dialog" aria-modal="true" aria-label="Rundgang">
      {/* Der Schleier. Ein Klick darauf beendet – wie Escape. */}
      <div
        className="absolute inset-0 bg-slate-900/60 transition-opacity"
        onClick={beenden}
        aria-hidden="true"
      />

      {/* Das hervorgehobene Element bleibt hell: ein Rahmen mit sehr großem
          Schlagschatten stanzt das Loch in den Schleier, ohne dass dafür ein
          zweites Overlay nötig wäre. */}
      {platz && (
        <div
          className="pointer-events-none absolute rounded-lg ring-2 ring-white/80 transition-all duration-200"
          style={{
            top: platz.oben - 4,
            left: platz.links - 4,
            width: platz.breite + 8,
            height: platz.hoehe + 8,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.6)',
          }}
        />
      )}

      <div
        className="absolute w-[320px] rounded-xl bg-white p-4 shadow-2xl"
        style={
          pos
            ? { top: pos.top, left: pos.left }
            : { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
        }
      >
        <div className="mb-2 flex items-start justify-between gap-2">
          <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
            {data?.offen === 'funktionen' && (
              <Sparkles className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
            )}
            {aktuell.titel}
          </p>
          <button
            onClick={beenden}
            className="-m-1 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Rundgang beenden"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <p className="text-sm leading-relaxed text-slate-600">{aktuell.text}</p>

        {data?.offen === 'funktionen' && nr === 0 && (
          <p className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
            {schritte.length === 1
              ? 'Diese Funktion ist neu für Sie freigeschaltet.'
              : `${schritte.length} Funktionen sind neu für Sie freigeschaltet.`}
          </p>
        )}

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1" aria-hidden="true">
            {schritte.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === nr ? 'w-4 bg-blue-600' : 'w-1.5 bg-slate-200'
                }`}
              />
            ))}
          </div>

          <div className="flex items-center gap-1">
            {nr > 0 && (
              <button
                onClick={() => setNr((n) => n - 1)}
                className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
                aria-label="Zurück"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {letzter ? (
              <button
                onClick={beenden}
                className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                <Check className="h-4 w-4" aria-hidden="true" />
                Fertig
              </button>
            ) : (
              <button
                onClick={weiter}
                className="flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                Weiter
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        {!letzter && (
          <button
            onClick={beenden}
            className="mt-2 w-full text-center text-xs text-slate-400 hover:text-slate-600"
          >
            Überspringen
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
